import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Copy, KeyRound, Plus } from "lucide-react";
import { apiKeyCreate } from "@shared/schemas/apiKeys";
import { evaluation, parseInput } from "@shared/validation/issues";
import { errorMessage } from "@web/lib/api";
import { apiKeyMetadataFindings } from "@web/lib/apiKeyMetadata";
import { useFormValidation } from "@web/lib/formValidation";
import { fmtStamp } from "@web/lib/format";
import { fmtSince } from "@web/lib/formatLeads";
import { leadSourcesQuery, venuesQuery } from "@web/lib/queries";
import { apiKeysQuery, leadApi, type ApiKeyRow } from "@web/lib/queries-leads";
import { useMe } from "@web/lib/session";
import { BottomSheet, Button, EmptyState, Inline, Page, Picker, Row, RowGroup, Skeleton, Stack, StatusChip, Text, TextField, UnsavedChanges, ValidationSummary, useToast } from "@web/ui";
import { AdminsOnly } from "./Settings";

/** Settings › API keys (SPEC §6.1 item 13, §8.1): list, create (plaintext shown once), revoke with confirm. Admins only. */
export function SettingsApiKeys() {
  const me = useMe();
  const keys = useQuery({ ...apiKeysQuery, enabled: me.companyAdmin });
  const venues = useQuery(venuesQuery(true));
  const sources = useQuery({ ...leadSourcesQuery(true), enabled: me.companyAdmin });
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<ApiKeyRow | null>(null);
  if (!me.companyAdmin) return <AdminsOnly />;
  const venueName = (id: string | null) => (id ? (venues.data?.find((v) => v.id === id)?.shortName ?? "?") : "Any venue");

  return (
    <Page>
      <Stack gap={3}>
        <Text variant="callout" tone="secondary">
          Keys let your websites post leads to venue365. The full key is shown once, when you create it.
        </Text>
        {keys.isError && <Stack gap={2}><Text tone="danger">API keys could not be loaded.</Text><Button variant="secondary" onClick={() => void keys.refetch()}>Retry keys</Button></Stack>}
        {(venues.isError || sources.isError) && <Stack gap={2}><Text tone="danger">Metadata checks unavailable. Previously loaded key metadata is still shown.</Text><Button variant="secondary" onClick={() => { void venues.refetch(); void sources.refetch(); }}>Retry metadata checks</Button></Stack>}
        {!keys.data ? (
          <Skeleton count={3} />
        ) : keys.data.length === 0 ? (
          <EmptyState icon={KeyRound} title="No API keys" body="Create one per website or integration." />
        ) : (
          <RowGroup>
            {keys.data.map((k) => {
              const metadata = apiKeyMetadataFindings(k, venues.data, sources.data);
              return (
              <Row
                key={k.id}
                icon={<KeyRound aria-hidden size={22} />}
                title={k.name}
                subtitle={`v365_${k.prefix}_… · ${venueName(k.defaultVenueId)} · ${k.lastUsedAt ? `used ${fmtSince(k.lastUsedAt)}` : "never used"}`}
                trailing={<Stack gap={1}><StatusChip tone={k.active ? "success" : "neutral"}>{k.active ? "Active" : "Revoked"}</StatusChip><StatusChip tone={venues.isError || sources.isError || metadata.issues.length ? "warn" : "neutral"}>{venues.isError || sources.isError ? "Metadata unavailable" : metadata.issues.length ? `Metadata · ${metadata.issues.length} to review` : "Metadata · partial"}</StatusChip></Stack>}
                onClick={() => setSelected(k)}
              />
            ); })}
          </RowGroup>
        )}
        <Button variant="secondary" icon={<Plus aria-hidden size={18} />} onClick={() => setCreating(true)}>
          Create key
        </Button>
      </Stack>
      {creating && <CreateKeySheet onClose={() => setCreating(false)} />}
      {selected && <KeySheet k={keys.data?.find((x) => x.id === selected.id) ?? selected} venueName={venueName} onClose={() => setSelected(null)} />}
    </Page>
  );
}

function CreateKeySheet({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const venues = useQuery(venuesQuery());
  const sources = useQuery(leadSourcesQuery());
  const [f, setF] = useState({ name: "", defaultVenueId: "", defaultSourceId: "" });
  const [created, setCreated] = useState<{ name: string; key: string } | null>(null);
  const body = { name: f.name, defaultVenueId: f.defaultVenueId || null, defaultSourceId: f.defaultSourceId || null, scopes: ["leads:write"] };
  const parsed = parseInput(apiKeyCreate, body, { type: "api_key" }, "api_key.save");
  const validation = useFormValidation(evaluation([...parsed.issues, ...apiKeyMetadataFindings(body, venues.data, sources.data).issues], false));
  const { busy, field } = validation;

  async function create() {
    if (!parsed.success || !validation.begin("api_key.save")) return;
    try {
      const r = await leadApi.createApiKey(parsed.data);
      setCreated({ name: r.name, key: r.key });
    } catch (e) {
      validation.reject(e);
      toast.error(errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  async function copy() {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.key);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed — select the key and copy it by hand");
    }
  }

  if (created)
    return (
      <BottomSheet
        open
        onClose={onClose}
        title={created.name}
        footer={
          <Button onClick={onClose}>
            Done
          </Button>
        }
      >
        <Stack gap={3}>
          <Text variant="callout" tone="danger">
            Copy this key now. It will not be shown again; if it's lost, revoke it and create a new one.
          </Text>
          <TextField label="API key" readOnly value={created.key} onFocus={(e) => e.currentTarget.select()} />
          <Button variant="secondary" icon={<Copy aria-hidden size={18} />} onClick={copy}>
            Copy key
          </Button>
          <Text variant="caption" tone="tertiary">
            Use it as `Authorization: Bearer …` on POST /v1/leads.
          </Text>
        </Stack>
      </BottomSheet>
    );

  return (
    <BottomSheet
      open
      onClose={() => { if (!busy) onClose(); }}
      title="Create API key"
      footer={
        <Button loading={busy} onClick={create}>
          Create key
        </Button>
      }
    >
      <Stack gap={3}>
        <UnsavedChanges dirty={!busy && Boolean(f.name || f.defaultVenueId || f.defaultSourceId)} />
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable || venues.isError || sources.isError} onRetry={venues.isError || sources.isError ? () => { void venues.refetch(); void sources.refetch(); } : undefined} />
        <TextField label="Name" placeholder="e.g. courtyardondauphin.com" value={f.name} onChange={(e) => validation.edit(() => setF({ ...f, name: e.target.value }))} autoFocus {...field("name")} />
        <Picker label="Default venue" placeholder="None — the payload names the venue" value={f.defaultVenueId} onChange={(v) => validation.edit(() => setF({ ...f, defaultVenueId: v }))} options={(venues.data ?? []).map((v) => ({ value: v.id, label: v.name }))} hint="Used when a submission doesn't say which venue" {...field("defaultVenueId")} />
        <Picker label="Default source" placeholder="None" value={f.defaultSourceId} onChange={(v) => validation.edit(() => setF({ ...f, defaultSourceId: v }))} options={(sources.data ?? []).map((s) => ({ value: s.id, label: s.name }))} {...field("defaultSourceId")} />
      </Stack>
    </BottomSheet>
  );
}

function KeySheet({ k, venueName, onClose }: { k: ApiKeyRow; venueName: (id: string | null) => string; onClose: () => void }) {
  const toast = useToast();
  const [confirm, setConfirm] = useState(false);
  const venues = useQuery(venuesQuery(true)), sources = useQuery(leadSourcesQuery(true));
  const saved = apiKeyMetadataFindings(k, venues.data, sources.data);
  const validation = useFormValidation(evaluation([], false));
  const { busy } = validation;

  async function revoke() {
    if (!validation.begin("api_key.save")) return;
    try {
      await leadApi.revokeApiKey(k.id);
      toast.success("Key revoked");
      onClose();
    } catch (e) {
      validation.reject(e);
      toast.error(errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  return (
    <BottomSheet
      open
      onClose={() => { if (!busy) onClose(); }}
      title={k.name}
      footer={
        k.active ? (
          confirm ? (
            <Stack gap={2}>
              <Button variant="destructive" loading={busy} onClick={revoke}>
                Yes, revoke — sites using it stop working
              </Button>
              <Button variant="secondary" disabled={busy} onClick={() => validation.edit(() => setConfirm(false))}>
                Keep it
              </Button>
            </Stack>
          ) : (
            <Button variant="destructive" onClick={() => setConfirm(true)}>
              Revoke key
            </Button>
          )
        ) : undefined
      }
    >
      <Stack gap={3}>
      <ValidationSummary result={saved} local loading={venues.isFetching || sources.isFetching} unavailable={venues.isError || sources.isError} title={venues.isError || sources.isError ? "Metadata checks unavailable" : "Saved key metadata · partial checks"} context="Only displayed metadata and loaded references are checked. Token validity and full server checks are not evaluated." onRetry={() => { void venues.refetch(); void sources.refetch(); }} />
      {(validation.unavailable || validation.result.issues.length > 0) && <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />}
      <RowGroup>
        <Row title="Prefix" subtitle={`v365_${k.prefix}_…`} />
        <Row title="Default venue" subtitle={venueName(k.defaultVenueId)} />
        <Row title="Scopes" subtitle={k.scopes.join(", ")} />
        <Row title="Last used" subtitle={k.lastUsedAt ? fmtStamp(k.lastUsedAt) : "Never"} />
        <Row title="Created" subtitle={fmtStamp(k.createdAt)} />
        <Row title="Status" trailing={<Inline gap={1}><StatusChip tone={k.active ? "success" : "neutral"}>{k.active ? "Active" : `Revoked ${fmtStamp(k.revokedAt)}`}</StatusChip></Inline>} />
      </RowGroup>
      </Stack>
    </BottomSheet>
  );
}
